import classNames from 'classnames'
import { FC, useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { TransitionGroup } from 'react-transition-group'

import { Box, Button, Card, CardActions, CardContent, Grid, Grow, Typography } from '@mui/material'

import { useGetTrackedGamesQuery } from '../../../../api/core'
import { CounterButton } from '../../../../components/CounterButton/CounterButton'
import { GRBanner } from '../../../../components/GRBanner/GRBanner'
import GRCircularProgress from '../../../../components/GRCircularProgress/GRCircularProgress'
import { LockedFeature } from '../../../../components/LockedFeature/LockedFeature'
import { useDocumentTitle } from '../../../../hooks/useDocumentTitle'
import { LockedFeatureId } from '../../../../types/LockedFeature'
import { useLiveEventsTrackerAccessCheck } from '../../../account/hooks/roleHooks'
import { useCurrentUserLanguage } from '../../../account/hooks/useCurrentUserLanguage'
import { Game, GameIcon } from '../../../game'
import { LiveEventsViewFilters } from '../../../live-events/components/LiveEventsViewFilters/LiveEventsViewFilters'
import { TrackedGamesSearchDialog } from '../../../live-events/components/TrackedGamesSearchDialog/TrackedGamesSearchDialog'
import { useFreeLiveEventsGamesArray } from '../../../live-events/hooks/useFreeLiveEventsGamesArray'
import { useLiveEventExamplesSearchParams } from '../../../live-events/hooks/useLiveEventExamplesSearchParams'
import { useLiveEventsCommonFilters } from '../../../live-events/hooks/useLiveEventsCommonFilters'
import { useLiveEventsFilteredData } from '../../../live-events/hooks/useLiveEventsFilteredData'
import { useDefaultLiveEvents } from '../../../live-events/hooks/useTrackedGamesEvents'
import { TrackedGame } from '../../../live-events/types/TrackedGame'
import { TrackingEventExample } from '../../../live-events/types/TrackingEvents'
import { LiveEventsList } from './LiveEventsList'

type LiveEventExamplesProps = {
  // Add props here if needed
}

export const LiveEventExamples: FC<LiveEventExamplesProps> = () => {
  const { t } = useTranslation()
  useDocumentTitle(t('sidebar:impl_examples'))
  const userLanguage = useCurrentUserLanguage()
  const { setSearchParams, parsedParams } = useLiveEventExamplesSearchParams()
  const [gameSearchDialogOpen, setGameSearchDialogOpen] = useState(false)
  const hasAccessToLiveEventsTracker = useLiveEventsTrackerAccessCheck()

  // Get tracked games from API
  const {
    data: allTrackedGames = [],
    isLoading: isTrackedGamesLoading,
    isFetching: isTrackedGamesFetching,
  } = useGetTrackedGamesQuery({
    userLanguage,
  })

  const freeLiveEventsGamesArray = useFreeLiveEventsGamesArray(allTrackedGames)

  // Filter tracked games based on selected game IDs from URL params
  // Include tracked games with permission or free live events games
  const trackedGames = useMemo(() => {
    return parsedParams.gameIds
      .map((gameId) => {
        const trackedGame = allTrackedGames?.find((tg) => tg.game.id === gameId && tg.permission)
        if (trackedGame) return trackedGame

        const freeGame = freeLiveEventsGamesArray?.find((g) => g.id === gameId)
        if (freeGame) {
          return {
            game: freeGame,
            permission: true,
            allowed: true,
            published: true,
            trackingStarted: 0,
            archetypes: [],
          } as TrackedGame
        }

        return null
      })
      .filter((game): game is TrackedGame => !!game)
  }, [allTrackedGames, parsedParams.gameIds, freeLiveEventsGamesArray])

  // Use tracked games directly instead of extracting just the games
  const games = trackedGames

  // Get tracked events for selected games
  const trackedEvents = useDefaultLiveEvents({ trackedGameIds: trackedGames.map((trackedGame) => trackedGame.game.id) })

  // Use trackedEvents directly since recurring field is now provided by the API
  const trackedEventExamples = trackedEvents

  // Use common filters hook
  const {
    commonFilters,
    commonFiltersData: originalCommonFiltersData,
    filterApplicability,
    liveEventNamesArray,
    handleCommonFiltersChange,
  } = useLiveEventsCommonFilters({
    selectedGameIds: trackedGames.map((trackedGame) => trackedGame.game.id),
    trackedEvents: trackedEventExamples,
    parsedParams: {
      ...parsedParams,
    },
    onFiltersChange: (filters) => {
      // Update URL with filter changes - only include parameters for visible filter groups
      setSearchParams((currentParams) => ({
        ...currentParams,
        liveEventTags: filters.liveEventTags.map((tag) => tag.id),
        liveEventSecondaryTags: filters.liveEventSecondaryTags.map((tag) => tag.id),
        motivations: Object.keys(filters.motivations).map((key) => key),
        archetypes: Object.keys(filters.archetypes).map((key) => key),
        liveEventAppearances: Object.keys(filters.liveEventAppearances),
      }))
    },
  })

  // Modify commonFiltersData to hide certain filter groups for LiveEventExamples
  const commonFiltersData = useMemo(
    () => ({
      ...originalCommonFiltersData,
      additionalDatas: [], // Hide game data filters
      liveEventDurations: [], // Hide duration filters
      liveEventPricePoints: [], // Hide price point filters
    }),
    [originalCommonFiltersData]
  )

  // Default filters for LiveEventExamples - all empty
  const defaultFilters = useMemo(
    () => ({
      liveEventTags: [],
      liveEventSecondaryTags: [],
      additionalDatas: {},
      liveEventDurations: {},
      liveEventAppearances: {},
      liveEventPricePoints: {},
      motivations: {},
      archetypes: {},
    }),
    []
  )

  // Use filtered data hook
  const { filteredTrackingEventsByGame, eventNameSearchValue, handleEventNameSearchValueChange } = useLiveEventsFilteredData({
    selectedGames: trackedGames,
    trackedEvents: trackedEventExamples,
    commonFilters,
    eventNameSearchValue: '',
  })

  // Convert tracked games array to map for easier access
  const selectedGamesMap = useMemo(() => {
    return games.reduce((acc, trackedGame) => ({ ...acc, [trackedGame.game.id]: trackedGame.game }), {})
  }, [games])

  const handleGameSearchDialogClose = useCallback(
    (games: Game[]) => {
      const newGameIds = games.map((game) => game.id)
      const currentGameIds = parsedParams.gameIds

      // Check if game selection actually changed
      const gameSelectionChanged = newGameIds.length !== currentGameIds.length || !newGameIds.every((id) => currentGameIds.includes(id))

      // Update URL with new game IDs and clear all filters only when game selection changes
      setSearchParams((current) => ({
        ...current,
        gameIds: newGameIds,
        ...(gameSelectionChanged && {
          liveEventTags: [],
          liveEventSecondaryTags: [],
          motivations: [],
          archetypes: [],
        }),
      }))
      setGameSearchDialogOpen(false)
    },
    [setSearchParams, parsedParams.gameIds]
  )

  const handleClearGames = useCallback(() => {
    // Clear games from URL
    setSearchParams((current) => ({ gameIds: [], liveEventTags: [], liveEventSecondaryTags: [], motivations: [], archetypes: [], liveEventAppearances: [] }))
  }, [setSearchParams])

  const selectedGamesCount = trackedGames.length
  const isLoadingGames = isTrackedGamesLoading || isTrackedGamesFetching
  const isLoading = isLoadingGames || trackedEvents.isLoading

  // Show locked feature if user doesn't have access to live events tracker
  if (!hasAccessToLiveEventsTracker) {
    return (
      <Box className="LiveEventExamples">
        <LockedFeature.Card lockedFeatureId={LockedFeatureId.LiveEventsTracker} />
      </Box>
    )
  }

  return (
    <Box className="LiveEventExamples">
      <Box sx={{ mb: 3 }}>
        <GRBanner severity="info">
          <Trans i18nKey="implementations:tab_live_event_examples_beta_tooltip" />
        </GRBanner>
      </Box>

      <Card className={classNames({ selected: true })}>
        <CardContent sx={{ pb: 0 }}>
          <Typography variant="h3" color="primary" gutterBottom>
            {t('implementations:label_live_event_examples_gamemode')}
          </Typography>
          <Typography mb={2}>
            <Trans i18nKey="implementations:label_description_live_event_examples_gamemode" />
          </Typography>

          <Grid container wrap="wrap" spacing={2} component={TransitionGroup}>
            {games.map((trackedGame) => (
              <Grow key={trackedGame.game.id}>
                <Grid item key={trackedGame.game.id}>
                  <GameIcon
                    src={trackedGame.game.getIcon() as string}
                    gameName={trackedGame.game.resolvedName}
                    size="normal"
                    appType={trackedGame.game.appType}
                    gamePlatforms={trackedGame.game.platforms}
                    discontinued={trackedGame.discontinued}
                  />
                </Grid>
              </Grow>
            ))}
          </Grid>
        </CardContent>
        <CardActions>
          <CounterButton
            loading={isTrackedGamesLoading}
            disabled={isTrackedGamesLoading}
            label={t('segment:filter_select_games')}
            count={selectedGamesCount}
            onClick={() => setGameSearchDialogOpen(true)}
          />
          <Button variant="contained" color="warning" size="large" onClick={handleClearGames} disabled={selectedGamesCount === 0 || isTrackedGamesLoading}>
            {t('common:clear')}
          </Button>
        </CardActions>
      </Card>
      {games.length > 0 && (
        <Card sx={{ mt: 2 }}>
          {isLoadingGames ? (
            <GRCircularProgress />
          ) : (
            <CardContent sx={{ pb: 0, mb: 0 }}>
              <Typography variant="h3" color="primary" gutterBottom>
                {t('implementations:label_filter_live_events')}
              </Typography>
              <Typography mb={2}>{t('implementations:label_description_filter_live_events')}</Typography>
              <LiveEventsViewFilters
                selectedGames={trackedGames}
                commonFilters={commonFilters}
                commonFiltersData={commonFiltersData}
                filterApplicability={filterApplicability}
                defaultFilters={defaultFilters}
                eventNameSearchValue={eventNameSearchValue}
                liveEventNames={liveEventNamesArray}
                onFiltersChange={handleCommonFiltersChange}
                onEventNameSearchValueChange={handleEventNameSearchValueChange}
              />
            </CardContent>
          )}
        </Card>
      )}

      <Box sx={{ mt: 3 }}>
        <LiveEventsList
          selectedGames={games}
          filteredEvents={filteredTrackingEventsByGame.events as { [gameId: string]: TrackingEventExample[] }}
          isLoading={isLoading}
        />
      </Box>
      {gameSearchDialogOpen && (
        <TrackedGamesSearchDialog
          open={gameSearchDialogOpen}
          onClose={handleGameSearchDialogClose}
          selectedGames={selectedGamesMap}
          maxGames={3}
          freeLiveEventsGames={freeLiveEventsGamesArray}
        />
      )}
    </Box>
  )
}
